import {defineStore} from 'pinia';
import {getVideoSrcFromFile} from '~/utils/video-editor/clip-utils.js';
import {useVideoEditorStoreSelection} from './selection.js';
import {useVideoEditorStoreSnapshots} from './snapshots.js';
import {useVideoEditorStoreMarkers} from './markers.js';
import {useVideoEditorStoreClips} from './clips.js';
import {useVideoEditorStoreSession} from './session.js';
import {useVideoEditorStoreSubmission} from './submission.js';

export const useVideoEditorStore = defineStore('videoEditorStore', () => {
  const videoElement = ref(null);
  const videoSrc = ref(null);
  const file = ref(null);
  const clips = ref([]);
  const jobStatus = ref(null);
  const clipNumber = ref(0);

  // --- Compose modules ---

  const selectionModule = useVideoEditorStoreSelection({clips});
  const {setActiveEditClip, clearActiveEditClip, activeEditClipId} = selectionModule;

  const snapshotsModule = useVideoEditorStoreSnapshots({clips, videoSrc, file});
  const {
    loadSnapshotSource,
    refreshMarkerSnapshot,
    refreshClipMarkerSnapshots,
    hydrateAllMarkerSnapshots,
    hydrateAllCustomFrameSnapshots,
    disposeSnapshots
  } = snapshotsModule;

  const markersModule = useVideoEditorStoreMarkers({
    clips,
    refreshMarkerSnapshot
  });

  const clipsModule = useVideoEditorStoreClips({
    clips, clipNumber, file,
    setActiveEditClip, clearActiveEditClip, activeEditClipId,
    refreshClipMarkerSnapshots
  });

  const sessionModule = useVideoEditorStoreSession({clips, file, jobStatus, clipNumber});
  const {startAutoPersist, stopAutoPersist, persistEditingSession} = sessionModule;

  const submissionModule = useVideoEditorStoreSubmission({
    clips, file,
    persistEditingSession, stopAutoPersist
  });

  // --- Top-level actions ---

  function initialize({file: f, videoElement: ve, videoSrc: vs}) {
    file.value = f;
    videoElement.value = ve;
    videoSrc.value = vs || getVideoSrcFromFile(f);

    if (videoSrc.value) {
      loadSnapshotSource(videoSrc.value);
    }

    startAutoPersist();

    void hydrateAllMarkerSnapshots();
    void hydrateAllCustomFrameSnapshots();
  }

  function setVideoSource({videoSrc: vs}) {
    videoSrc.value = vs || getVideoSrcFromFile(file.value);

    if (!videoSrc.value) {
      return;
    }

    loadSnapshotSource(videoSrc.value);
    void hydrateAllMarkerSnapshots();
  }

  function clear() {
    stopAutoPersist();
    disposeSnapshots();
    videoElement.value = null;
    videoSrc.value = null;
    clips.value = [];
    clipNumber.value = 0;
    jobStatus.value = null;
    clearActiveEditClip();
  }

  return {
    videoElement,
    videoSrc,
    file,
    clips,
    jobStatus,
    clipNumber,

    ...selectionModule,
    ...snapshotsModule,
    ...markersModule,
    ...clipsModule,
    ...sessionModule,
    ...submissionModule,

    initialize,
    setVideoSource,
    clear
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useVideoEditorStore, import.meta.hot));
}
